/* eslint-disable indent */
'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe } from 'lucide-react';
import Image from 'next/image';
import { useLanguage, languages } from '@/app/context/language-context';
import { useTheme } from '../theme-provider';

export default function LanguageSelector({ currentPage = 'home' }) {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme } = useTheme();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]);

  // Get current language details
  const currentLanguage =
    languages.find((lang) => lang.code === language) || languages[0];

  return (
    <div className='relative' ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center space-x-1 px-2 rounded-md font-medium hover:text-black hover:bg-gray-100 transition-colors
         dark:hover:bg-secondarycolor ${
           isScrolled
             ? 'dark:hover:text-black hover:bg-primarycolor'
             : 'dark:hover:text-black dark:text-white hover:bg-primarycolor'
         }`}
        aria-label='Select language'
      >
        <Globe
          className={`h-5 w-5 mr-1 ml-2 ${
            currentPage === 'home'
              ? isScrolled
                ? 'text-black dark:text-white'
                : 'text-white dark:text-white'
              : 'text-black dark:text-white'
          } hover:text-primarycolor`}
        />
        <span
          className={`py-3 text-lg rounded-md font-bold ${
            isMobileMenuOpen
              ? 'text-black'
              : currentPage === 'home'
                ? isScrolled
                  ? 'text-black dark:text-white'
                  : 'text-white'
                : 'text-black dark:text-white'
          }`}
        >
          <Image
            src={`https://images.x-space.ae/assets/images/website_image/flags/${currentLanguage.code}.webp`}
            width={16}
            height={12}
            alt={currentLanguage.name}
            unoptimized // Remove this line if the image is from a domain allowed in `next.config.js`
          />
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className='absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 rounded-md shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-gray-700'
          >
            <div className='py-1'>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setIsOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-2 text-sm text-left ${
                    language === lang.code
                      ? 'bg-gray-100 dark:bg-gray-800 text-primarycolor dark:text-secondarycolor'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className='mr-2 ml-2'>
                    <Image
                      src={`https://images.x-space.ae/assets/images/website_image/flags/${lang.code}.webp`}
                      width={16}
                      height={12}
                      alt={lang.name}
                      unoptimized // Optional: remove if domain is whitelisted in next.config.js
                    />
                  </span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
